import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert'
import { Button } from '@repo/ui/button'
import { Field, FieldContent, FieldError, FieldLabel } from '@repo/ui/field'
import { Input } from '@repo/ui/input'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { BrandPage, BrandRow } from '@/routes/-components/brand-canvas'
import { readLastOrganization } from '@/routes/-lib/last-organization'

const searchSchema = z.object({
	returnPath: z.string().optional(),
	// Set by the backend's OIDC callback redirect on a failed exchange
	// (`/login?error=<code>&requestId=<id>`). `error` is a stable machine
	// code, not shown as-is — the message here is the same for every code,
	// because there is nothing a person can do differently for one versus
	// another; `requestId` is what support asks for.
	error: z.string().optional(),
	requestId: z.string().optional(),
	// Both set by `/o/$slug/login` when the organization it was sent to does
	// not accept a sign-in: the name comes back to the field it was typed in
	// rather than to an error page, because a name that does not resolve is
	// a typo and the remedy is the input, not a reload.
	slug: z.string().optional(),
	unknownOrganization: z.boolean().optional(),
	// Set by the «change organization» link on Keycloak's sign-in page: the
	// one visit that must show this form even with an organization
	// remembered, because changing it is why the person came.
	changeOrganization: z.boolean().optional()
})

const slugFormSchema = z.object({
	slug: z.string().trim().min(1)
})

/**
 * The redirect target of the `(private)` session gate. A guard can't know
 * which organization an anonymous visitor belongs to, so it lands here first
 * — this page's only job is collecting the organization slug and forwarding
 * to `/o/$slug/login`, which is what actually starts the OIDC round trip
 * (the slug is the pre-login realm-resolution key, `/o/:slug/...`).
 *
 * With an organization remembered from the last sign-in (OPSSTD-9930) it is
 * not shown at all: the browser goes straight on, and the person meets one
 * page, Keycloak's, with the way back here on it. It stays when it has
 * something to say — a failed callback or a refused name, where skipping it
 * would loop on the same failure — and when the person asked for it.
 */
export const Route = createFileRoute('/(signed-out)/login/')({
	validateSearch: searchSchema,
	beforeLoad: ({ search }) => {
		if (search.error || search.unknownOrganization) {
			return
		}
		if (search.changeOrganization) {
			return
		}

		const remembered = readLastOrganization()
		if (remembered) {
			// `replace`: back from Keycloak must leave, not land here and
			// bounce straight onto Keycloak again.
			throw redirect({
				to: '/o/$slug/login',
				params: { slug: remembered },
				search: search.returnPath
					? { returnPath: search.returnPath }
					: {},
				replace: true
			})
		}
	},
	component: LoginPage
})

function LoginPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { returnPath, error, requestId, slug, unknownOrganization } =
		Route.useSearch()
	const form = useForm({
		resolver: zodResolver(slugFormSchema),
		defaultValues: { slug: slug ?? readLastOrganization() ?? '' }
	})

	// The refusal stands until the field changes, and then it is stale: what
	// is on screen is no longer the name the server turned down.
	const refusedSlug = form.watch('slug') === slug
	const unknown = unknownOrganization === true && refusedSlug

	const onSubmit = form.handleSubmit(({ slug }) => {
		navigate({
			to: '/o/$slug/login',
			params: { slug },
			search: returnPath ? { returnPath } : {}
		})
	})

	return (
		<BrandPage>
			<BrandRow />
			<h1 className='font-semibold text-2xl tracking-tight'>
				{t('client:login.title')}
			</h1>
			<p className='mt-2 text-muted-foreground text-sm'>
				{t('client:login.description')}
			</p>
			{error && (
				<Alert className='mt-6' variant='destructive'>
					<AlertTitle>
						{t('client:login.oidc_callback_failed')}
					</AlertTitle>
					{requestId && (
						<AlertDescription>
							{t('client:login.request_id', { requestId })}
						</AlertDescription>
					)}
				</Alert>
			)}
			<form className='mt-6 flex flex-col gap-6' onSubmit={onSubmit}>
				<Field data-invalid={!!form.formState.errors.slug || unknown}>
					<FieldLabel htmlFor='slug'>
						{t('client:login.slug_label')}
					</FieldLabel>
					<FieldContent>
						<Input
							aria-invalid={
								!!form.formState.errors.slug || unknown
							}
							id='slug'
							placeholder={t('client:login.slug_placeholder')}
							{...form.register('slug')}
						/>
					</FieldContent>
					<FieldError
						errors={[
							form.formState.errors.slug,
							unknown
								? {
										message: t(
											'client:login.unknown_organization'
										)
									}
								: undefined
						]}
					/>
				</Field>
				<Button type='submit'>{t('client:login.continue')}</Button>
			</form>
		</BrandPage>
	)
}
